import { http } from "../../../utils/axios/axios";
import { getToken, parseJwt } from "../../onboarding/utils";
import { get } from "lodash";

/**
 * MDRT Network Layer
 */

// Prefer agent-specific APIM endpoint; fallback to generic APIM base URL
// Normalize behaviour:
// - If REACT_APP_APIM_AGENT is a full URL (startsWith http), use it (trim trailing slash)
// - If it's a path like '/agent/coedev', join it with REACT_APP_APIM_BASE_URL
// - Otherwise fallback to REACT_APP_APIM_BASE_URL
const APIM_AGENT_ENV = get(window, "envConfig.REACT_APP_APIM_AGENT", "");
const APIM_BASE_ENV = get(window, "envConfig.REACT_APP_APIM_BASE_URL", "");

export const APIM_AGENT_BASE = `${APIM_BASE_ENV}${APIM_AGENT_ENV}`;

const getSurveyDataUrl = "/api/surveys/";

/**
 * Get authentication headers for API requests
 */
const getAuthHeaders = () => {
  const token = getToken();
  // Prefer dedicated subscription key, fallback to OEM key
  const apimSubscriptionKey = get(
    window,
    "envConfig.REACT_APP_APIM_SUBSCRIPTION_KEY",
    get(window, "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY", ""),
  );
  return {
    Authorization: token,
    "Ocp-Apim-Subscription-Key": apimSubscriptionKey,
    "Content-Type": "application/json",
  };
};

/**
 * Get current user agent code from token
 */
const getCurrentUserAgentCode = (): string | null => {
  try {
    const token = getToken();
    if (!token) return null;

    const tokenData = parseJwt(token);
    return tokenData?.agentCode || tokenData?.sub || null;
  } catch (error) {
    console.error("Failed to parse token:", error);
    return null;
  }
};

export interface ActivityLog {
  createdAt: string;
  action: string;
  lastQuestion?: string;
}

export interface SurveyResultResponse {
  _id: string;
  agentCode: string;
  surveyId: string;
  prospectId: string | null;
  prospectName: string | null;
  activityLogs: ActivityLog[];
  resultStatus: string;
  preferLanguage: string;
  programId: string;
  dueDate: string;
  party: string;
  partyId: string;
  short_id: string;
  createdAt: string;
  updatedAt: string;
  body: Record<string, any>;
  submitDate?: string;
}

export interface GetAssignmentDetailResponse {
  assignmentId: string;
  surveyId: string;
  agentCode: string;
  status: string;
  surveyBody: any; // The survey template/definition
  surveyResults: Array<{
    _id: string;
    assignmentId: string;
    surveyId: string;
    agentCode: string;
    status: string;
    body: any; // The survey answers
    createdAt: string;
    updatedAt: string;
  }>;
  readonly: boolean;
}
export interface GetAssessmentQuestionsResponse {
  assessmentId: string;
  schema: any;
  isReadonly?: boolean;
  answers?: Record<string, any>;
}

export interface SubmitSurveyResponse {
  success: boolean;
  submittedAt: string;
  assessmentId: string;
  results?: {
    scores: Record<string, number>;
    recommendations: string[];
  };
  insightsAvailable: boolean;
}

export interface LogSurveyResponse {
  success: boolean;
  eventId: string;
  timestamp: string;
}

export enum ActivityLogAction {
  viewed = "viewed",
  closedWithoutAnswer = "closedWithoutAnswer",
}

export interface SurveyStatusProps {
  activityLogAction: ActivityLogAction;
  activityLogLastQuestion?: string;
}

/**
 * Get assignment detail by assignmentId
 * This provides surveyId, surveyBody (template), and existing answers for the assignment
 */
export const getAssignmentDetail = async (
  assignmentId: string,
): Promise<GetAssignmentDetailResponse> => {
  try {
    // Real API call to get assignment detail including surveyBody and surveyResults
    const url = `${APIM_AGENT_BASE}/agents/me/customer-surveys/_/results/${assignmentId}`;
    const response = await http.get(url, {
      headers: getAuthHeaders(),
    });

    const data = response.data;
    const currentUserAgentCode = getCurrentUserAgentCode();

    // Determine readonly mode based on survey results status and user permissions
    const firstResult = data.surveyResults?.[0];
    const isSubmitted =
      firstResult?.resultStatus === "submitted" || data.resultStatus === "submitted";
    const isMentor = currentUserAgentCode !== firstResult?.partyId;
    const readonly = isSubmitted || isMentor;

    return {
      assignmentId: assignmentId,
      surveyId: data.surveyId,
      agentCode: firstResult?.partyId || "",
      status: firstResult?.resultStatus || data.resultStatus || "pending",
      surveyBody: data.surveyBody, // The survey template
      surveyResults: data.surveyResults || [],
      readonly,
    };
  } catch (error) {
    console.error("Failed to fetch assignment detail:", error);
    throw new Error(`Failed to fetch assignment detail: ${error}`);
  }
};

/**
 * Get assessment template by surveyId
 * This provides the survey structure and questions
 */
export const getAssessmentTemplate = async (surveyId: string): Promise<any> => {
  try {
    // Real API call to get assessment template
    const url = `${APIM_AGENT_BASE}/agents/me/customer-surveys/${surveyId}/preview`;
    const response = await http.get(url, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error) {
    console.error("Failed to fetch assessment template:", error);
    throw new Error(`Failed to fetch assessment template: ${error}`);
  }
};

export const getAssessmentQuestions = async (
  assignmentId: string,
): Promise<GetAssessmentQuestionsResponse> => {
  try {
    // Use single API call to get assignment detail (includes both template and answers)
    const assignmentDetail = await getAssignmentDetail(assignmentId);

    // Extract survey template from surveyBody
    const surveyTemplate = assignmentDetail.surveyBody;

    // Extract existing answers from surveyResults (use first result if available)
    const firstResult = assignmentDetail.surveyResults?.[0];
    const existingAnswers = firstResult?.body || {};

    // Configure survey schema with existing answers and readonly mode
    const schema = {
      ...surveyTemplate,
      pages:
        surveyTemplate.pages?.map((page: any) => ({
          ...page,
          elements: page.elements?.map((element: any) => {
            if (element.type === "radiogroup") {
              return {
                ...element,
                preset: "radio",
                isRequired: !assignmentDetail.readonly, // Don't require answers in readonly mode
                showClearButton: false,
                readOnly: assignmentDetail.readonly, // Set readonly mode for individual questions
              };
            }
            return {
              ...element,
              readOnly: assignmentDetail.readonly,
            };
          }),
        })) || [],
      showCompletedPage: false,
      showProgressBar: "top",
      progressBarType: "pages",
      requiredText: "",
      questionTitlePattern: "numTitle",
      mode: assignmentDetail.readonly ? "display" : "edit", // Set survey mode
    };

    return {
      assessmentId: assignmentDetail.surveyId,
      schema,
      isReadonly: assignmentDetail.readonly,
      answers: existingAnswers, // Pre-filled answers from surveyResults.body
    };
  } catch (error) {
    console.error("Failed to fetch assessment questions:", error);
    throw new Error(`Failed to fetch assessment questions: ${error}`);
  }
};

export const submitSurveyAnswers = async (
  surveyId: string,
  assignmentId: string,
  answers: any,
): Promise<SubmitSurveyResponse> => {
  try {
    // Use real API to submit assignment answers
    const url = `${APIM_AGENT_BASE}/agents/me/customer-surveys/results/${assignmentId}`;
    const response = await http.patch(
      url,
      {
        body: answers,
        status: "submitted",
      },
      {
        headers: getAuthHeaders(),
      },
    );

    const resultData: SurveyResultResponse = response.data;
    const submitResponse: SubmitSurveyResponse = {
      success: true,
      submittedAt: resultData.submitDate || new Date().toISOString(),
      assessmentId: surveyId,
      insightsAvailable: true,
    };

    console.log("KASH Assessment Submission:", {
      surveyId,
      assignmentId,
      answers,
      response: resultData,
    });

    return submitResponse;
  } catch (error) {
    console.error("Failed to submit survey answers:", error);
    throw new Error("Unable to submit assessment. Please check your connection and try again.");
  }
};

/**
 * Log survey activity event using axios (normal request)
 * Use this for normal event logging (e.g., page view)
 * Updates the assignment via PATCH API with activity log
 * This method waits for the response and returns a Promise
 */
export const windowEvent = async (
  assignmentId: string,
  data: SurveyStatusProps,
): Promise<LogSurveyResponse> => {
  try {
    // Convert activity log action to structured log entry
    const activityLogEntry = {
      createdAt: new Date().toISOString(),
      action: data.activityLogAction,
      lastQuestion: data.activityLogLastQuestion,
    };

    // Use PATCH API to update assignment with activity log
    const url = `${APIM_AGENT_BASE}/agents/me/customer-surveys/results/${assignmentId}`;
    const response = await http.patch(
      url,
      {
        activityLogs: [activityLogEntry],
      },
      {
        headers: getAuthHeaders(),
      },
    );

    return {
      success: true,
      eventId: response.data?.eventId || `event_${Date.now()}`,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Failed to log survey event:", error);
    throw new Error("Unable to log survey event.");
  }
};

/**
 * Log survey activity event using fetch with keepalive (fire-and-forget)
 * Use this ONLY for page unload/close events to ensure the request completes
 * even if the page is being closed
 *
 * Updates the assignment via PATCH API with activity log
 *
 * Technical Note:
 * - Uses native fetch API with keepalive option
 * - keepalive allows the request to complete even after page unload
 * - Cannot use axios because XMLHttpRequest doesn't support keepalive
 * - This is a browser-level guarantee for request completion
 */
export const fetchWindowEvent = (assignmentId: string, data: SurveyStatusProps): void => {
  try {
    const apiUrl = `${APIM_AGENT_BASE}/agents/me/customer-surveys/results/${assignmentId}`;

    // Convert activity log action to structured log entry
    const activityLogEntry = {
      createdAt: new Date().toISOString(),
      action: data.activityLogAction,
      lastQuestion: data.activityLogLastQuestion,
    };

    const token = getToken();
    const apimSubscriptionKey = get(
      window,
      "envConfig.REACT_APP_APIM_SUBSCRIPTION_KEY",
      get(window, "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY", ""),
    );

    // Use fetch with keepalive for reliable event sending during page unload
    fetch(apiUrl, {
      method: "PATCH",
      body: JSON.stringify({
        activityLogs: [activityLogEntry],
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
        "Ocp-Apim-Subscription-Key": apimSubscriptionKey,
      },
      keepalive: true, // Critical: allows request to complete even after page unload
    }).catch((err) => {
      console.warn("Failed to send keepalive event:", err);
    });
  } catch (error) {
    console.warn("Failed to send keepalive event:", error);
  }
};

export const fireAnalyticsEvent = async (
  assessmentId: string,
  eventData: {
    eventType: string;
    timestamp: string;
    metadata?: Record<string, any>;
  },
): Promise<void> => {
  try {
    console.log("Analytics Event:", { assessmentId, ...eventData });
  } catch (error) {
    console.warn("Analytics event failed:", error);
  }
};
